import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment, { Moment } from 'moment-timezone';
import { Period } from '@spike/model';

import styles from './RoomsOperating.module.scss';
import RoomsOperatingTable from './RoomsOperatingTable';
import RoomsHeaderSummary from './RoomsHeaderSummary';
import Button from 'components/UI/V3/Button/Button';
import { faFileArrowDown } from '@fortawesome/pro-regular-svg-icons';

import { RootState } from 'store';
import { getRoomsOperatingReport } from 'actions/insights/bookings/roomsOperatingReport/RoomsOperatingReportActions';
import { useTimeZone } from 'hooks';
import type { RoomsOperatingPeriods } from 'model/RoomsOperatingReport';
import { buildRoomsOperatingCSV, downloadCSV, defaultRoomsOperatingFilename } from './roomsOperatingExport';

interface RoomsOperatingProps {
    description?: string;
}

export const RoomsOperating: FunctionComponent<RoomsOperatingProps> = ({ description }) => {
    const dispatch = useDispatch();
    const timeZone = useTimeZone();

    const { report, loading } = useSelector((state: RootState) => state.roomsOperatingReport);

    /**
     * Base "today"
     */
    const today = useMemo(() => moment().tz(timeZone).startOf('day'), [timeZone]);

    /**
     * Init all dates
     */
    const [periods, setPeriods] = useState<RoomsOperatingPeriods>(() => {
        const period: Period = {
            from: today.clone().startOf('day'),
            to: today.clone().endOf('day')
        };

        return {
            period,
            compareToday: today.clone().subtract(1, 'year'),

            compareMtd: {
                from: today.clone().startOf('month').subtract(1, 'year'),
                to: today.clone().endOf('day').subtract(1, 'year')
            },

            compareYtd: {
                from: today.clone().startOf('year').subtract(1, 'year'),
                to: today.clone().endOf('day').subtract(1, 'year')
            }
        };
    });

    useEffect(() => {
        dispatch(
            getRoomsOperatingReport({
                start: periods.period.from.format('YYYY-MM-DD'),
                end: periods.period.to.format('YYYY-MM-DD'),

                compare_today: periods.compareToday.format('YYYY-MM-DD'),

                compare_mtd_start: periods.compareMtd.from.format('YYYY-MM-DD'),
                compare_mtd_end: periods.compareMtd.to.format('YYYY-MM-DD'),

                compare_ytd_start: periods.compareYtd.from.format('YYYY-MM-DD'),
                compare_ytd_end: periods.compareYtd.to.format('YYYY-MM-DD')
            })
        );
    }, [dispatch, periods]);

    const handlePeriodChange = (next: Period | null) => {
        if (!next) return;

        setPeriods(prev => ({
            ...prev,
            period: {
                from: next.from.clone().tz(timeZone).startOf('day'),
                to: next.to.clone().tz(timeZone).endOf('day')
            }
        }));
    };

    const handleCompareTodayChange = (next: Moment | null) => {
        if (!next) return;

        setPeriods(prev => ({
            ...prev,
            compareToday: next.clone().tz(timeZone).startOf('day')
        }));
    };

    const handleCompareMtdChange = (next: Period | null) => {
        if (!next) return;

        setPeriods(prev => ({
            ...prev,
            compareMtd: {
                from: next.from.clone().tz(timeZone).startOf('day'),
                to: next.to.clone().tz(timeZone).endOf('day')
            }
        }));
    };

    const handleCompareYtdChange = (next: Period | null) => {
        if (!next) return;

        setPeriods(prev => ({
            ...prev,
            compareYtd: {
                from: next.from.clone().tz(timeZone).startOf('day'),
                to: next.to.clone().tz(timeZone).endOf('day')
            }
        }));
    };

    const handleExport = () => {
        if (!report) return;
        const csv = buildRoomsOperatingCSV(report, periods, timeZone);
        downloadCSV(csv, defaultRoomsOperatingFilename(periods, timeZone));
    };

    return (
        <div className={styles.container}>
            {description && (
                <div>
                    <span className={styles.description}>{description}</span>
                </div>
            )}

            <div className={styles.exportContainer}>
                <Button
                    label="Export"
                    variant="black"
                    leftIcon={faFileArrowDown}
                    disabled={loading || !report}
                    onClick={handleExport}
                />
            </div>

            <div className={styles.summaryCardsContainer}>
                <RoomsHeaderSummary
                    loading={loading}
                    data={report?.summary ?? null}
                />
            </div>

            <div className={styles.tableContainer}>
                <RoomsOperatingTable
                    loading={loading}
                    report={report}
                    periods={periods}
                    timeZone={timeZone}
                    onPeriodChange={handlePeriodChange}
                    onCompareTodayChange={handleCompareTodayChange}
                    onCompareMtdChange={handleCompareMtdChange}
                    onCompareYtdChange={handleCompareYtdChange}
                />
            </div>
        </div>
    );
};

export default RoomsOperating;
